import { useEffect, useState } from 'react';
import { FiFilter, FiSearch } from 'react-icons/fi';
import { MdOutlineAddBox } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ProviderCardNew } from '../../components';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { useAppDispatch } from '../../redux/hooks';
import { updateHttpsProvider } from '../../redux/httpsProviders/actions';
import { updateActiveStep } from '../../redux/providerBuilder/stepperDetails';
import { getAllUserProvidersWithPagination } from '../../utils/app-builder/fetch-providers';
import { useDebounce } from '../../utils/logs/send-logs';
import { ProviderDetails } from '../explore/components/provider-details.component';

export const MyProvidersScreen = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [myProviderOptions, setMyProviderOptions] = useState<any>([]);
  const debouncedSearchTerm = useDebounce(searchQuery, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { width, height } = useWindowDimensions();

  // State for filters
  const [providerTypeFilter, setProviderTypeFilter] = useState('ALL');
  const [providerStatusFilter, setProviderStatusFilter] = useState('ALL');
  const [providerVisibilityFilter, setProviderVisibilityFilter] = useState('ALL');
  const [selectedProviderData, setSelectedProviderData] = useState<any>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const getPageSizeForScreen = () => {
    if (width < 1480 && width >= 1000) {
      return 4;
    } else if (width < 1696 && width >= 1480) {
      return 6;
    } else if (width < 2000 && width >= 1696) {
      return 8;
    } else if (width < 2300 && width >= 2000) {
      return 12;
    } else {
      return 16;
    }
  };

  const pageSize = getPageSizeForScreen() + 4;

  const fetchProviderData = async (pageKey: number) => {
    const sortByCreatedLatest = true;
    const myProviders = await getAllUserProvidersWithPagination(
      pageKey * pageSize,
      pageSize,
      searchQuery,
      providerTypeFilter,
      providerStatusFilter,
      providerVisibilityFilter,
      sortByCreatedLatest
    );
    setMyProviderOptions([...myProviders?.providers]);
    setTotalPages(Math.ceil(myProviders?.count / pageSize));
  };

  useEffect(() => {
    fetchProviderData(currentPage - 1);
  }, [
    debouncedSearchTerm,
    currentPage,
    providerTypeFilter,
    providerStatusFilter,
    providerVisibilityFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    providerTypeFilter,
    providerStatusFilter,
    providerVisibilityFilter,
  ]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          className="x-small-font"
          onClick={() => handlePageChange(i)}
          bgColor={currentPage === i ? 'whitesmoke' : 'white'}
        >
          {i}
        </Button>
      );
    }

    return pages;
  };

  const createNewProvider = () => {
    dispatch(updateActiveStep(0));
    navigate('/provider-builder');
  };

  const handleProviderDelete = async (httpProviderId: string) => {
    const status: any = await dispatch(
      updateHttpsProvider({
        httpProviderId: httpProviderId,
        formData: { isActive: false },
      })
    );

    if (status?.payload?.isSuccess) {
      toast({
        title: 'Provider status updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchProviderData(currentPage - 1);
    }
  };

  const showProviderDetails = (providerId: string) => {
    const providerData = myProviderOptions.find(
      (provider: any) => provider.httpProviderId === providerId
    );
    setSelectedProviderData(providerData);
    onOpen();
  };

  return (
    <Box display="flex" h="fit-content" justifyContent="center" width="100%">
      <Card
        w={width > 1740 ? '1300px' : '95%'}
        mt={6}
        variant="outline"
        borderRadius="12px"
        h="fit-content"
        boxShadow="sm"
      >
        <CardBody p={0}>
          <VStack align="flex-start" padding="32px" spacing="24px">
            <VStack align="flex-start" spacing="16px" w="100%">
              <VStack align="flex-start" spacing="4px">
                <Text
                  fontFamily="inter"
                  className="medium-font"
                  fontWeight="bold"
                  color="black"
                >
                  My Providers
                </Text>
                <Text
                  color="gray.600"
                  className="small-font"
                  whiteSpace="pre-line"
                  fontFamily="inter"
                >
                  Providers you have built
                </Text>
              </VStack>
              <HStack spacing="10px" w="100%" justifyContent="space-between">
                <Flex w="65%" gap={5} alignItems="center">
                  <InputGroup w="500px">
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray" />
                    </InputLeftElement>
                    <Input
                      size="md"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      type="search"
                      placeholder="Search providers"
                      className="small-font"
                      borderRadius="8px"
                      variant="filled"
                      bgColor="#f3f5fc"
                      py={5}
                    />
                  </InputGroup>
                </Flex>
                <Flex alignItems="center" gap={5} w="35%" justifyContent="flex-end">
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      colorScheme="blue"
                      rightIcon={<FiFilter size={18} color="black" />}
                      className="small-font"
                      borderRadius="8px"
                      variant="outline"
                      size="md"
                      fontWeight={500}
                      color="black"
                      _hover={{
                        transform: 'scale(1.1)',
                      }}
                    >
                      Filter
                    </MenuButton>
                    <MenuList minWidth="350px">
                      <Flex direction="column">
                        <Flex>
                          <div
                            style={{
                              borderRight: '1px solid #e2e8f0',
                              width: '50%',
                              padding: '10px',
                            }}
                          >
                            <MenuOptionGroup
                              defaultValue={providerTypeFilter ?? 'ALL'}
                              title="Provider Type"
                              type="radio"
                              onChange={(value) => setProviderTypeFilter(value as string)}
                            >
                              <MenuItemOption value="ALL">All</MenuItemOption>
                              <MenuItemOption value="VERIFIED">Verified</MenuItemOption>
                              <MenuItemOption value="UNVERIFIED">
                                Not Verified
                              </MenuItemOption>
                            </MenuOptionGroup>
                          </div>
                          <div style={{ width: '50%', padding: '10px' }}>
                            <MenuOptionGroup
                              defaultValue={providerVisibilityFilter ?? 'ALL'}
                              onChange={(value) =>
                                setProviderVisibilityFilter(value as string)
                              }
                              title="Visibility Status"
                              type="radio"
                            >
                              <MenuItemOption value="ALL">All</MenuItemOption>
                              <MenuItemOption value="PUBLIC">Public</MenuItemOption>
                              <MenuItemOption value="PRIVATE">Private</MenuItemOption>
                            </MenuOptionGroup>
                          </div>
                        </Flex>
                        <Flex borderTop="1px solid #e2e8f0">
                          <div style={{ width: '50%', padding: '10px' }}>
                            <MenuOptionGroup
                              defaultValue={providerStatusFilter ?? 'ALL'}
                              onChange={(value) =>
                                setProviderStatusFilter(value as string)
                              }
                              title="Provider Status"
                              type="radio"
                            >
                              <MenuItemOption value="ALL">All</MenuItemOption>
                              <MenuItemOption value="ACTIVE">Active</MenuItemOption>
                              <MenuItemOption value="INACTIVE">Inactive</MenuItemOption>
                            </MenuOptionGroup>
                          </div>
                        </Flex>
                      </Flex>
                    </MenuList>
                  </Menu>
                  <Button
                    leftIcon={<MdOutlineAddBox size={18} color="white" />}
                    className="small-font"
                    borderRadius="8px"
                    onClick={createNewProvider}
                    variant="outline"
                    bgColor="black"
                    _hover={{
                      bgColor: 'black',
                      transform: 'scale(1.1)',
                    }}
                    color="white"
                    size="md"
                    fontWeight={500}
                  >
                    New Provider
                  </Button>
                </Flex>
              </HStack>
            </VStack>
            <Divider />
            {myProviderOptions.length === 0 ? (
              <Text className="small-font" fontFamily="inter" color="gray.500">
                No providers found
              </Text>
            ) : (
              <Grid
                w="100%"
                templateColumns="repeat(auto-fill,minmax(255px,1fr))"
                gap={height < 1000 ? '16px' : '24px'}
              >
                {myProviderOptions.map((provider: any) => (
                  <GridItem key={provider.httpProviderId}>
                    <ProviderCardNew
                      providerDetail={provider}
                      pointerCursor={true}
                      showEditButton={true}
                      isSelected={false}
                      showDeleteButton={true}
                      handleProviderDelete={handleProviderDelete}
                      onSelect={showProviderDetails}
                    />
                  </GridItem>
                ))}
              </Grid>
            )}

            <HStack
              w="100%"
              padding="8px"
              justifyContent="space-between"
              alignItems="center"
              mt={4}
            >
              <Text fontFamily="inter" className="x-small-font" color="gray.600">
                Page {currentPage} of {totalPages}
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(1)}
                  isDisabled={currentPage === 1}
                >
                  <ArrowLeftIcon boxSize={3} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                >
                  <ChevronLeftIcon boxSize={4} />
                </Button>
                {renderPageNumbers()}
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                >
                  <ChevronRightIcon boxSize={4} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="x-small-font"
                  onClick={() => handlePageChange(totalPages)}
                  isDisabled={currentPage === totalPages}
                >
                  <ArrowRightIcon boxSize={3} />
                </Button>
              </HStack>
            </HStack>
            {selectedProviderData && (
              <ProviderDetails
                onClose={onClose}
                isOpen={isOpen}
                providerDetail={selectedProviderData}
              />
            )}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};
